import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import Icons from '../components/Icons';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';
import MessageService from '../services/MessageService';
import ReminderService from '../services/ReminderService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import InventoryKpis from '../components/inventory/InventoryKpis';
import InventoryFilterBar from '../components/inventory/InventoryFilterBar';
import InventoryTable from '../components/inventory/InventoryTable';
import ItemDetailDrawer from '../components/inventory/ItemDetailDrawer';
import BulkActionBar from '../components/inventory/BulkActionBar';
import {
  buildInventoryCounts,
  filterInventoryItems,
  sortInventoryItems,
  statusLabels
} from '../components/inventory/inventoryUtils';

const buildCsv = (headers, rows) => {
  return [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n');
};

const downloadCsv = (filename, headers, rows) => {
  const csv = buildCsv(headers, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function InventoryPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { inventoryItems, inventoryController, students } = useStudentStore();
  const staffService = useMemo(() => new StaffService(), []);
  const reminderService = useMemo(() => new ReminderService(), []);
  const messageService = useMemo(() => new MessageService(), []);
  const staffList = useMemo(() => safeList(staffService), [staffService]);
  const canManageInventory = ['admin', 'inventory'].includes(user?.accessScope);

  const [filters, setFilters] = useState({
    search: '',
    types: [],
    statuses: [],
    assignment: 'all',
    condition: 'all',
    due: 'any'
  });
  const [activeKpi, setActiveKpi] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeItemId, setActiveItemId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [assignItem, setAssignItem] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    assignedToType: 'student',
    assignedToId: '',
    checkoutDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    expectedReturnDate: '',
    notes: ''
  });

  const assignmentOptions = useMemo(() => {
    const list = assignmentForm.assignedToType === 'student' ? students : staffList;
    return list.map((person) => ({
      value: person.id,
      label: person.name,
      meta: assignmentForm.assignedToType === 'student'
        ? person.reference || person.id
        : person.role || person.title || ''
    }));
  }, [assignmentForm.assignedToType, staffList, students]);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    type: '',
    serialNumber: '',
    condition: 'Good',
    status: 'available',
    notes: ''
  });

  const [reminderModal, setReminderModal] = useState(null);
  const [reminderMessage, setReminderMessage] = useState('');
  const [requestItem, setRequestItem] = useState(null);
  const [requestForm, setRequestForm] = useState({ dueDate: '', reason: '' });
  const [requestStatus, setRequestStatus] = useState('');

  const inventoryTypes = useMemo(() => {
    return [...new Set(inventoryItems.map((item) => item.type))].sort();
  }, [inventoryItems]);

  const inventoryConditions = useMemo(() => {
    return [...new Set(inventoryItems.map((item) => item.condition).filter(Boolean))].sort();
  }, [inventoryItems]);

  const statusOptions = useMemo(
    () => [
      { value: 'available', label: statusLabels.available },
      { value: 'checked_out', label: statusLabels.checked_out },
      { value: 'under_repair', label: statusLabels.under_repair },
      { value: 'retired', label: statusLabels.retired }
    ],
    []
  );

  const filteredItems = useMemo(
    () => filterInventoryItems({ items: inventoryItems, filters, inventoryController }),
    [inventoryItems, filters, inventoryController]
  );

  const sortedItems = useMemo(
    () => sortInventoryItems(filteredItems, inventoryController),
    [filteredItems, inventoryController]
  );

  const counts = useMemo(
    () => buildInventoryCounts(inventoryItems, inventoryController),
    [inventoryItems, inventoryController]
  );

  const visibleIds = useMemo(() => sortedItems.map((item) => item.id), [sortedItems]);
  const selectedItems = useMemo(
    () => inventoryItems.filter((item) => selectedIds.has(item.id)),
    [inventoryItems, selectedIds]
  );

  const activeItem = useMemo(
    () => inventoryItems.find((item) => item.id === activeItemId) || null,
    [inventoryItems, activeItemId]
  );

  const activeFilters = useMemo(() => {
    const list = [];
    if (filters.search) {
      list.push({ id: 'search', label: `Search: ${filters.search}`, type: 'search' });
    }
    filters.types.forEach((type) => list.push({ id: `type-${type}`, label: `Type: ${type}`, type: 'type', value: type }));
    filters.statuses.forEach((status) =>
      list.push({
        id: `status-${status}`,
        label: `Status: ${statusLabels[status] || status}`,
        type: 'status',
        value: status
      })
    );
    if (filters.assignment !== 'all') {
      list.push({
        id: 'assignment',
        label: `Assignment: ${filters.assignment === 'assigned' ? 'Assigned' : 'Unassigned'}`,
        type: 'assignment'
      });
    }
    if (filters.condition !== 'all') {
      list.push({ id: 'condition', label: `Condition: ${filters.condition}`, type: 'condition' });
    }
    if (filters.due !== 'any') {
      list.push({
        id: 'due',
        label: `Due: ${filters.due === 'overdue' ? 'Overdue' : 'Due soon'}`,
        type: 'due'
      });
    }
    return list;
  }, [filters]);

  const updateFilters = (updater) => {
    setActiveKpi(null);
    setFilters((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
  };

  const clearFilters = () => {
    setActiveKpi(null);
    setFilters({
      search: '',
      types: [],
      statuses: [],
      assignment: 'all',
      condition: 'all',
      due: 'any'
    });
  };

  const handleRemoveFilter = (filter) => {
    if (filter.type === 'search') {
      updateFilters({ search: '' });
    } else if (filter.type === 'type') {
      updateFilters((prev) => ({ ...prev, types: prev.types.filter((type) => type !== filter.value) }));
    } else if (filter.type === 'status') {
      updateFilters((prev) => ({ ...prev, statuses: prev.statuses.filter((status) => status !== filter.value) }));
    } else if (filter.type === 'assignment') {
      updateFilters({ assignment: 'all' });
    } else if (filter.type === 'condition') {
      updateFilters({ condition: 'all' });
    } else if (filter.type === 'due') {
      updateFilters({ due: 'any' });
    }
  };

  const applyKpiFilter = (kpiId) => {
    setActiveKpi((prev) => {
      const next = prev === kpiId ? null : kpiId;
      setFilters((prevFilters) => {
        const base = { ...prevFilters, statuses: [], due: 'any' };
        if (!next || next === 'total') return base;
        if (next === 'available') return { ...base, statuses: ['available'] };
        if (next === 'checkedOut') return { ...base, statuses: ['checked_out'] };
        if (next === 'overdue') return { ...base, due: 'overdue' };
        if (next === 'underRepair') return { ...base, statuses: ['under_repair'] };
        return base;
      });
      return next;
    });
  };

  const openReminder = (item) => {
    const activeAssignment = inventoryController.getActiveAssignment(item);
    if (!activeAssignment) return;
    const staff = staffList.find(
      (member) =>
        member.id === activeAssignment.assignedToId || member.name === activeAssignment.assignedToName
    );
    const entityId = `${item.id}:${activeAssignment.id}`;
    const lastReminder = staff ? reminderService.getLastReminder(entityId, staff.id) : null;
    setReminderMessage('');
    setReminderModal({ item, assignment: activeAssignment, staff, lastReminder });
  };

  const sendReminder = (force = false) => {
    if (!reminderModal) return;
    if (!reminderModal.staff) {
      setReminderMessage('No staff email configured for this assignment.');
      return;
    }
    const result = reminderService.sendInventoryReminder({
      item: reminderModal.item,
      assignment: reminderModal.assignment,
      staffId: reminderModal.staff.id,
      forced: force
    });
    if (result.status === 'throttled') {
      setReminderMessage(result.error);
      return;
    }
    setReminderMessage('Reminder queued.');
    setReminderModal(null);
  };

  const openAssignModal = (item) => {
    setAssignmentForm((prev) => ({
      ...prev,
      assignedToType: 'student',
      assignedToId: '',
      checkoutDate: new Date().toISOString().slice(0, 10),
      dueDate: '',
      expectedReturnDate: '',
      notes: ''
    }));
    setAssignItem(item);
  };

  const openRequestModal = (item) => {
    setRequestForm({ dueDate: '', reason: '' });
    setRequestStatus('');
    setRequestItem(item);
  };

  const handleAssign = () => {
    if (!assignItem || !assignmentForm.assignedToId) return;
    const assignedToName =
      assignmentForm.assignedToType === 'student'
        ? students.find((student) => student.id === assignmentForm.assignedToId)?.name
        : staffList.find((staff) => staff.id === assignmentForm.assignedToId)?.name;

    inventoryController.assignItem(
      assignItem.id,
      {
        ...assignmentForm,
        assignedToName: assignedToName || ''
      },
      user
    );
    setAssignItem(null);
  };

  const handleRequestCheckout = () => {
    if (!requestItem) return;
    if (!requestForm.reason.trim()) {
      setRequestStatus('Add a reason for the request.');
      return;
    }
    const request = {
      id: `req-${Math.random().toString(36).slice(2, 9)}`,
      requestedById: user?.id || 'staff',
      requestedByName: user?.name || 'Staff',
      requestedAt: new Date().toISOString(),
      dueDate: requestForm.dueDate || '',
      reason: requestForm.reason.trim(),
      status: 'pending'
    };
    const existing = requestItem.requests || [];
    inventoryController.updateItem(
      requestItem.id,
      { requests: [request, ...existing] },
      user
    );

    const recipients = staffList
      .filter((staff) => ['admin', 'inventory'].includes(staff.accessScope))
      .map((staff) => ({ id: staff.id, name: staff.name, type: 'staff' }));
    if (recipients.length > 0) {
      messageService.send({
        subject: `Inventory request: ${requestItem.name}`,
        body: [
          `${user?.name || 'Staff'} requested to check out ${requestItem.name}.`,
          requestForm.dueDate ? `Requested due date: ${requestForm.dueDate}` : 'No due date provided.',
          `Reason: ${requestForm.reason.trim()}`
        ].join('\n'),
        fromUser: user || { id: 'system', name: 'SafeguardPro' },
        recipients,
        references: [{ type: 'inventory', id: requestItem.id, label: requestItem.name, route: `/inventory/${requestItem.id}` }]
      });
    }

    setRequestStatus('Request sent to inventory admin.');
    setRequestItem(null);
  };

  const handleApproveRequest = (item, request) => {
    if (!item || !request) return;
    if (item.status !== 'available') {
      window.alert('This item is not available for checkout.');
      return;
    }
    const assignment = {
      assignedToType: 'staff',
      assignedToId: request.requestedById,
      assignedToName: request.requestedByName,
      checkoutDate: new Date().toISOString().slice(0, 10),
      dueDate: request.dueDate || '',
      notes: `Approved request ${request.id}`
    };
    const updatedItem = inventoryController.assignItem(item.id, assignment, user);
    const assignmentId = updatedItem?.assignments?.[0]?.id || '';
    const updatedRequests = (updatedItem?.requests || []).map((entry) =>
      entry.id === request.id
        ? {
            ...entry,
            status: 'approved',
            approvedAt: new Date().toISOString(),
            approvedById: user?.id,
            approvedByName: user?.name,
            assignmentId
          }
        : entry
    );
    inventoryController.updateItem(item.id, { requests: updatedRequests }, user);

    const recipient =
      staffList.find((staff) => staff.id === request.requestedById) || {
        id: request.requestedById,
        name: request.requestedByName || 'Staff'
      };
    messageService.send({
      subject: `Inventory request approved: ${item.name}`,
      body: [
        `Your request to check out ${item.name} has been approved.`,
        assignment.dueDate ? `Due date: ${assignment.dueDate}` : 'No due date set.',
        'Please collect the item from storage.'
      ].join('\n'),
      fromUser: user || { id: 'system', name: 'SafeguardPro' },
      recipients: [{ id: recipient.id, name: recipient.name, type: 'staff' }],
      references: [{ type: 'inventory', id: item.id, label: item.name, route: `/inventory/${item.id}` }]
    });
  };

  const handleDenyRequest = (item, request) => {
    if (!item || !request) return;
    const reason = window.prompt('Reason for denying this request (optional):', '') || '';
    const updatedRequests = (item.requests || []).map((entry) =>
      entry.id === request.id
        ? {
            ...entry,
            status: 'denied',
            deniedAt: new Date().toISOString(),
            deniedById: user?.id,
            deniedByName: user?.name,
            decisionReason: reason
          }
        : entry
    );
    inventoryController.updateItem(item.id, { requests: updatedRequests }, user);

    const recipient =
      staffList.find((staff) => staff.id === request.requestedById) || {
        id: request.requestedById,
        name: request.requestedByName || 'Staff'
      };
    messageService.send({
      subject: `Inventory request denied: ${item.name}`,
      body: [
        `Your request to check out ${item.name} was denied.`,
        reason ? `Reason: ${reason}` : 'Please contact the inventory team for details.'
      ].join('\n'),
      fromUser: user || { id: 'system', name: 'SafeguardPro' },
      recipients: [{ id: recipient.id, name: recipient.name, type: 'staff' }],
      references: [{ type: 'inventory', id: item.id, label: item.name, route: `/inventory/${item.id}` }]
    });
  };

  const handleCheckIn = (item, assignment) => {
    if (!assignment) return;
    inventoryController.checkInItem(item.id, assignment.id, user);
  };

  const openEditModal = (item) => {
    setEditForm({
      name: item.name,
      type: item.type,
      serialNumber: item.serialNumber || '',
      condition: item.condition || 'Good',
      status: item.status || 'available',
      notes: item.notes || ''
    });
    setEditItem(item);
  };

  const handleSaveEdit = () => {
    if (!editItem) return;
    inventoryController.updateItem(
      editItem.id,
      {
        name: editForm.name,
        type: editForm.type,
        serialNumber: editForm.serialNumber,
        condition: editForm.condition,
        status: editForm.status,
        notes: editForm.notes
      },
      user
    );
    setEditItem(null);
  };

  const handleAddNote = (item, note) => {
    if (!item || note === item.notes) return;
    inventoryController.updateItem(item.id, { notes: note }, user);
  };

  const handleMarkRepair = (item) => {
    if (inventoryController.getActiveAssignment(item)) return;
    inventoryController.updateItem(item.id, { status: 'under_repair' }, user);
  };

  const handleMarkAvailable = (item) => {
    if (inventoryController.getActiveAssignment(item)) return;
    inventoryController.updateItem(item.id, { status: 'available' }, user);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = (isAllSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleRowClick = (item) => {
    setActiveItemId(item.id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setActiveItemId(null);
    setDrawerOpen(false);
  };

  const exportItems = (items, filenameSuffix = 'inventory') => {
    const headers = ['ID', 'Reference', 'Name', 'Type', 'Status', 'Condition', 'Assigned To', 'Due Date'];
    const rows = items.map((item) => {
      const assignment = inventoryController.getActiveAssignment(item);
      return [
        item.id,
        item.reference,
        item.name,
        item.type,
        statusLabels[item.status] || item.status,
        item.condition,
        assignment ? `${assignment.assignedToName} (${assignment.assignedToType})` : 'Unassigned',
        assignment?.dueDate || ''
      ];
    });
    downloadCsv(`${filenameSuffix}-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const bulkEligibility = useMemo(() => {
    const hasSelection = selectedItems.length > 0;
    const allCheckedOut = hasSelection && selectedItems.every((item) => inventoryController.getActiveAssignment(item));
    const canRepair =
      hasSelection &&
      selectedItems.every((item) => item.status !== 'retired' && !inventoryController.getActiveAssignment(item));
    const canAvailable =
      hasSelection &&
      selectedItems.every((item) => item.status !== 'retired' && !inventoryController.getActiveAssignment(item));
    return {
      allCheckedOut,
      canRepair,
      canAvailable
    };
  }, [selectedItems, inventoryController]);

  const bulkActions = canManageInventory
    ? [
        {
          id: 'checkin',
          label: 'Check in',
          variant: 'primary',
          disabled: !bulkEligibility.allCheckedOut,
          disabledReason: bulkEligibility.allCheckedOut ? '' : 'Select only checked-out items.',
          onClick: () =>
            selectedItems.forEach((item) => {
              const assignment = inventoryController.getActiveAssignment(item);
              if (assignment) inventoryController.checkInItem(item.id, assignment.id, user);
            })
        },
        {
          id: 'repair',
          label: 'Mark under repair',
          disabled: !bulkEligibility.canRepair,
          disabledReason: bulkEligibility.canRepair ? '' : 'Check in items before marking under repair.',
          onClick: () =>
            selectedItems.forEach((item) => {
              inventoryController.updateItem(item.id, { status: 'under_repair' }, user);
            })
        },
        {
          id: 'available',
          label: 'Mark available',
          disabled: !bulkEligibility.canAvailable,
          disabledReason: bulkEligibility.canAvailable ? '' : 'Check in items before marking available.',
          onClick: () =>
            selectedItems.forEach((item) => {
              inventoryController.updateItem(item.id, { status: 'available' }, user);
            })
        },
        {
          id: 'export',
          label: 'Export selected',
          variant: 'secondary',
          disabled: selectedItems.length === 0,
          disabledReason: selectedItems.length === 0 ? 'Select items to export.' : '',
          onClick: () => exportItems(selectedItems, 'inventory-selected')
        }
      ]
    : [
        {
          id: 'export',
          label: 'Export selected',
          variant: 'secondary',
          disabled: selectedItems.length === 0,
          disabledReason: selectedItems.length === 0 ? 'Select items to export.' : '',
          onClick: () => exportItems(selectedItems, 'inventory-selected')
        }
      ];

  return (
    <div className="space-y-4">
      <div className="sp-inventory-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Inventory Workspace</h2>
          <p className="sp-muted">
            {counts.total} items · {counts.checkedOut} checked out · {counts.overdue} overdue
          </p>
        </div>
        <div className="sp-actions">
          <Button size="sm" variant="secondary" onClick={() => exportItems(sortedItems, 'inventory')}>
            Export CSV
          </Button>
          {canManageInventory && (
            <Button size="sm" onClick={() => navigate('/inventory/new')}>
              <Icons.Plus /> Add Item
            </Button>
          )}
        </div>
      </div>

      <InventoryKpis counts={counts} activeKpi={activeKpi} onSelect={applyKpiFilter} />

      <InventoryFilterBar
        search={filters.search}
        onSearchChange={(value) => updateFilters({ search: value })}
        filters={filters}
        typeOptions={inventoryTypes}
        statusOptions={statusOptions}
        conditionOptions={inventoryConditions}
        resultsCount={sortedItems.length}
        activeFilters={activeFilters}
        onToggleType={(type) =>
          updateFilters((prev) => ({
            ...prev,
            types: prev.types.includes(type) ? prev.types.filter((entry) => entry !== type) : [...prev.types, type]
          }))
        }
        onToggleStatus={(status) =>
          updateFilters((prev) => ({
            ...prev,
            statuses: prev.statuses.includes(status)
              ? prev.statuses.filter((entry) => entry !== status)
              : [...prev.statuses, status]
          }))
        }
        onAssignmentChange={(value) => updateFilters({ assignment: value })}
        onConditionChange={(value) => updateFilters({ condition: value })}
        onDueChange={(value) => updateFilters({ due: value })}
        onClearFilters={clearFilters}
        onRemoveFilter={handleRemoveFilter}
      />

      <BulkActionBar selectedCount={selectedItems.length} actions={bulkActions} />

      <div className="sp-inventory-layout">
        <div className="space-y-3">
          {inventoryItems.length === 0 ? (
            <div className="sp-panel sp-panel--muted p-6">
              <p className="sp-value" style={{ fontWeight: 600 }}>No items in inventory</p>
              <p className="text-sm sp-muted mt-2">Add your first item to start tracking assignments.</p>
              {canManageInventory && (
                <Button size="sm" className="mt-4" onClick={() => navigate('/inventory/new')}>
                  <Icons.Plus /> Add Item
                </Button>
              )}
            </div>
          ) : (
            <InventoryTable
              items={sortedItems}
              inventoryController={inventoryController}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onRowClick={handleRowClick}
              onViewItem={(item) => navigate(`/inventory/${item.id}`)}
              onEditItem={canManageInventory ? openEditModal : null}
              onSendReminder={canManageInventory ? openReminder : null}
              canManage={canManageInventory}
            />
          )}
        </div>

        <div className="sp-inventory-panel">
          <ItemDetailDrawer
            item={activeItem}
            inventoryController={inventoryController}
            isOpen={drawerOpen}
            onClose={closeDrawer}
            onCheckOut={canManageInventory ? openAssignModal : null}
            onRequestCheckout={canManageInventory ? null : openRequestModal}
            onApproveRequest={canManageInventory ? handleApproveRequest : null}
            onDenyRequest={canManageInventory ? handleDenyRequest : null}
            onCheckIn={canManageInventory ? handleCheckIn : null}
            onMarkRepair={canManageInventory ? handleMarkRepair : null}
            onMarkAvailable={canManageInventory ? handleMarkAvailable : null}
            onEdit={canManageInventory ? openEditModal : null}
            onAddNote={canManageInventory ? handleAddNote : null}
            onViewHistory={(item) => navigate(`/inventory/${item.id}`)}
            onSendReminder={canManageInventory ? openReminder : null}
            canManage={canManageInventory}
          />
        </div>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />}

      <Modal isOpen={Boolean(assignItem)} onClose={() => setAssignItem(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Check out item</h3>
              <p className="text-sm sp-muted">Assign this item to a student or staff member.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAssignItem(null)}>Close</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Assigned to type</label>
              <select
                className="sp-select w-full"
                value={assignmentForm.assignedToType}
                onChange={(event) =>
                  setAssignmentForm((prev) => ({ ...prev, assignedToType: event.target.value, assignedToId: '' }))
                }
              >
                <option value="student">Student</option>
                <option value="staff">Staff</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Assigned to</label>
              <SearchableSelect
                value={assignmentForm.assignedToId}
                options={assignmentOptions}
                placeholder={`Search ${assignmentForm.assignedToType === 'student' ? 'students' : 'staff'}`}
                emptyLabel={`No ${assignmentForm.assignedToType === 'student' ? 'students' : 'staff'} found.`}
                ariaLabel="Search assignee"
                onChange={(value) => setAssignmentForm((prev) => ({ ...prev, assignedToId: value }))}
              />
            </div>
            <div>
              <label className="sp-label">Checkout date</label>
              <DateSelect
                className="w-full mt-2"
                value={assignmentForm.checkoutDate}
                onChange={(value) => setAssignmentForm((prev) => ({ ...prev, checkoutDate: value }))}
              />
            </div>
            <div>
              <label className="sp-label">Due date</label>
              <DateSelect
                className="w-full mt-2"
                value={assignmentForm.dueDate}
                onChange={(value) => setAssignmentForm((prev) => ({ ...prev, dueDate: value }))}
              />
            </div>
            <div>
              <label className="sp-label">Expected return</label>
              <DateSelect
                className="w-full mt-2"
                value={assignmentForm.expectedReturnDate}
                onChange={(value) => setAssignmentForm((prev) => ({ ...prev, expectedReturnDate: value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Notes</label>
              <textarea
                className="sp-textarea w-full"
                rows={3}
                value={assignmentForm.notes}
                onChange={(event) => setAssignmentForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignItem(null)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!assignmentForm.assignedToId}>Check out</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(requestItem)} onClose={() => setRequestItem(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Request checkout</h3>
              <p className="text-sm sp-muted">Send a request to the inventory admin to check out this item.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setRequestItem(null)}>Close</Button>
          </div>
          <div className="sp-panel sp-panel--muted p-3 text-sm">
            <div className="sp-value" style={{ fontWeight: 600 }}>{requestItem?.name}</div>
            <div className="text-xs sp-muted">{requestItem?.reference} · {requestItem?.type}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Requested due date</label>
              <DateSelect
                className="w-full mt-2"
                value={requestForm.dueDate}
                onChange={(value) => setRequestForm((prev) => ({ ...prev, dueDate: value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Reason</label>
              <textarea
                className="sp-textarea w-full"
                rows={3}
                value={requestForm.reason}
                onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))}
                placeholder="Why do you need this item?"
              />
            </div>
          </div>
          {requestStatus && <div className="text-xs sp-warning-text">{requestStatus}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRequestItem(null)}>Cancel</Button>
            <Button onClick={handleRequestCheckout} disabled={!requestForm.reason.trim()}>
              Send request
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(editItem)} onClose={() => setEditItem(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Edit item</h3>
              <p className="text-sm sp-muted">Update inventory details.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setEditItem(null)}>Close</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Item name</label>
              <input
                className="sp-input w-full"
                value={editForm.name}
                onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <input
                className="sp-input w-full"
                value={editForm.type}
                onChange={(event) => setEditForm((prev) => ({ ...prev, type: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Serial number</label>
              <input
                className="sp-input w-full"
                value={editForm.serialNumber}
                onChange={(event) => setEditForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Condition</label>
              <select
                className="sp-select w-full"
                value={editForm.condition}
                onChange={(event) => setEditForm((prev) => ({ ...prev, condition: event.target.value }))}
              >
                {['Good', 'Fair', 'Damaged', 'Lost'].map((condition) => (
                  <option key={condition} value={condition}>{condition}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                className="sp-select w-full"
                value={editForm.status}
                onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Notes</label>
              <textarea
                className="sp-textarea w-full"
                rows={3}
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.name.trim()}>Save changes</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(reminderModal)} onClose={() => setReminderModal(null)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Send overdue reminder</h3>
              <p className="text-sm sp-muted">Uses the reminder template configured in Settings.</p>
            </div>
            <Button variant="ghost" onClick={() => setReminderModal(null)}>Close</Button>
          </div>
          {reminderModal && (
            <div className="sp-panel sp-panel--muted p-3 text-sm">
              <div className="sp-value" style={{ fontWeight: 600 }}>{reminderModal.item.name}</div>
              <div className="text-xs sp-muted mt-1">
                Assigned to {reminderModal.staff?.name || reminderModal.assignment?.assignedToName || 'Unassigned'}
                {reminderModal.assignment?.dueDate ? ` · Due ${formatDate(reminderModal.assignment.dueDate)}` : ''}
              </div>
              {reminderModal.lastReminder && (
                <div className="text-xs sp-muted mt-1">
                  Last reminder: {formatDateTime(reminderModal.lastReminder.sentAt)}
                </div>
              )}
              {!reminderModal.staff?.loginEmail && (
                <div className="text-xs sp-warning-text mt-1">No staff email configured.</div>
              )}
            </div>
          )}
          {reminderMessage && <div className="text-xs sp-warning-text">{reminderMessage}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReminderModal(null)}>Cancel</Button>
            <Button
              onClick={() => sendReminder(false)}
              disabled={
                reminderModal?.staff
                  ? reminderService.isThrottled(
                      `${reminderModal.item.id}:${reminderModal.assignment?.id}`,
                      reminderModal.staff.id
                    )
                  : true
              }
            >
              Send reminder
            </Button>
            <Button variant="ghost" onClick={() => sendReminder(true)} disabled={!reminderModal?.staff}>
              Resend now
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
